import React from 'react';
import { Input, Button, Radio, Checkbox, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { RadioChangeEvent } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import dayjs from 'dayjs';

interface QueryParamsProps {
  supplierFrom: string;
  supplierTo: string;
  dateFrom: string;
  dateTo: string;
  deptFrom: string;
  deptTo: string;
  balanceFilter: number;
  includeUnapproved: boolean;
  reportType: string;
  loading: boolean;
  onSupplierFromChange: (v: string) => void;
  onSupplierToChange: (v: string) => void;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onDeptFromChange: (v: string) => void;
  onDeptToChange: (v: string) => void;
  onBalanceFilterChange: (v: number) => void;
  onIncludeUnapprovedChange: (v: boolean) => void;
  onReportTypeChange: (v: string) => void;
  onQuery: () => void;
}

const QueryParams: React.FC<QueryParamsProps> = ({
  supplierFrom, supplierTo, dateFrom, dateTo, deptFrom, deptTo,
  balanceFilter, includeUnapproved, reportType, loading,
  onSupplierFromChange, onSupplierToChange,
  onDateFromChange, onDateToChange,
  onDeptFromChange, onDeptToChange,
  onBalanceFilterChange, onIncludeUnapprovedChange,
  onReportTypeChange, onQuery,
}) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
      {/* Row 1: Supplier range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>供應商代號[從]</span>
          <Input
            value={supplierFrom}
            onChange={(e) => onSupplierFromChange(e.target.value)}
            placeholder="起"
            style={{ width: 120 }}
            maxLength={12}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={supplierTo}
            onChange={(e) => onSupplierToChange(e.target.value)}
            placeholder="迄"
            style={{ width: 120 }}
            maxLength={12}
          />
        </div>
      </div>

      {/* Row 2: Date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>入帳日期[從]</span>
          <DatePicker
            value={dateFrom ? dayjs(dateFrom) : null}
            onChange={(_date, dateString) => onDateFromChange(typeof dateString === 'string' ? dateString : '')}
            format="YYYY-MM-DD"
            style={{ width: 140 }}
            placeholder="起"
          />
          <span className="text-sm">[至]</span>
          <DatePicker
            value={dateTo ? dayjs(dateTo) : null}
            onChange={(_date, dateString) => onDateToChange(typeof dateString === 'string' ? dateString : '')}
            format="YYYY-MM-DD"
            style={{ width: 140 }}
            placeholder="迄"
          />
        </div>
      </div>

      {/* Row 3: Department range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>部門代號[從]</span>
          <Input
            value={deptFrom}
            onChange={(e) => onDeptFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 80 }}
            maxLength={4}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={deptTo}
            onChange={(e) => onDeptToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 80 }}
            maxLength={4}
          />
        </div>
      </div>

      {/* Row 4: Balance filter + Include unapproved */}
      <div className="flex flex-wrap items-center gap-6 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">餘額篩選</span>
          <Radio.Group
            value={balanceFilter}
            onChange={(e: RadioChangeEvent) => onBalanceFilterChange(e.target.value as number)}
          >
            <Radio value={1}>全部列印</Radio>
            <Radio value={2}>只顯示有餘額</Radio>
          </Radio.Group>
        </div>
        <Checkbox
          checked={includeUnapproved}
          onChange={(e: CheckboxChangeEvent) => onIncludeUnapprovedChange(e.target.checked)}
        >
          包括未核准單據
        </Checkbox>
      </div>

      {/* Row 5: Report type checkboxes */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <span className="text-sm font-medium">報表類型</span>
        <Radio.Group
          value={reportType}
          onChange={(e: RadioChangeEvent) => onReportTypeChange(e.target.value as string)}
        >
          <Radio value="supplier">廠商別</Radio>
          <Radio value="dept">部門別</Radio>
          <Radio value="buyer">採購員別</Radio>
          <Radio value="memo">異常說明</Radio>
        </Radio.Group>
      </div>

      {/* Query button */}
      <div className="flex gap-2">
        <Button type="primary" icon={<SearchOutlined />} onClick={onQuery} loading={loading}>
          查詢
        </Button>
      </div>
    </div>
  );
};

export default QueryParams;
